/**
 * Join Organization Page
 * 
 * Landing page for invite link URLs (/join/:code).
 * Handles the process of joining an organization via invite link.
 */

import { tokens } from '@healthdesk/shared-utils';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  Divider,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import LoginIcon from '@mui/icons-material/Login';
import { useAuth } from '@healthdesk/shared-hooks';
import { chartmindAdminService } from '../services/chartmind';

/**
 * Get initials from a name
 */
const getInitials = (name) => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * Generate a color from string
 */
const stringToColor = (str) => {
  if (!str) return '#607d8b';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [tokens.colors.chartMindNodeDefault.light, '#388e3c', '#d32f2f', '#7b1fa2', '#00796b'];
  return colors[Math.abs(hash) % colors.length];
};

const JoinOrganization = ({ codeFromToken }) => {
  const { code: codeParam, token: tokenParam } = useParams();
  const code = codeFromToken ?? tokenParam ?? codeParam;
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [joinResult, setJoinResult] = useState(null);

  // Load invite data
  useEffect(() => {
    const loadInviteData = async () => {
      if (!code) {
        setError('Invalid invite link');
        setIsLoading(false);
        return;
      }

      try {
        const result = await chartmindAdminService.findAdminByInviteCode(code);
        
        if (!result) {
          setError('This invite link is invalid or has been deleted.');
          setIsLoading(false);
          return;
        }

        const { admin, inviteLink } = result;

        // Check if expired
        if (inviteLink.expiresAt && new Date(inviteLink.expiresAt) < new Date()) {
          setError('This invite link has expired.');
          setIsLoading(false);
          return;
        }

        // Check max uses
        if (inviteLink.maxUses !== null && inviteLink.usedCount >= inviteLink.maxUses) {
          setError('This invite link has reached its usage limit.');
          setIsLoading(false);
          return;
        }

        setInviteData({
          admin,
          inviteLink,
          displayName: admin.facilityName || admin.organizationName || admin.name || 'Organization',
        });
      } catch (err) {
        console.error('[JoinOrganization] Error loading invite:', err);
        setError('Failed to load invite information.');
      } finally {
        setIsLoading(false);
      }
    };

    loadInviteData();
  }, [code]);

  // Handle join
  const handleJoin = useCallback(async () => {
    if (!user?.email || !code) return;

    setIsJoining(true);
    setError(null);

    try {
      const result = await chartmindAdminService.useInviteLink(code, user.email);
      setJoinResult(result);
    } catch (err) {
      console.error('[JoinOrganization] Error joining:', err);
      setError('Failed to join organization. Please try again.');
    } finally {
      setIsJoining(false);
    }
  }, [user, code]);

  // Redirect to login if not authenticated
  const handleLogin = () => {
    // Store the invite code for after login
    sessionStorage.setItem('pendingInviteCode', code);
    navigate('/login');
  };

  // Check for pending invite after login
  useEffect(() => {
    const pendingCode = sessionStorage.getItem('pendingInviteCode');
    if (user && pendingCode && pendingCode === code) {
      sessionStorage.removeItem('pendingInviteCode');
      // Auto-join after login
      handleJoin();
    }
  }, [user, code, handleJoin]);

  // Loading state
  if (authLoading || isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading invite information...</Typography>
      </Container>
    );
  }

  // Error state
  if (error && !inviteData) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Invalid Invite Link
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Go Home
          </Button>
        </Paper>
      </Container>
    );
  }

  // Success state
  if (joinResult?.success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          {joinResult.requiresApproval ? (
            <>
              <PendingIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Request Submitted
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Your request to join <strong>{inviteData.displayName}</strong> has been submitted.
                You'll receive access once the administrator approves your request.
              </Typography>
            </>
          ) : (
            <>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Welcome!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                You've successfully joined <strong>{inviteData.displayName}</strong>.
                You can now use their customized ChartMind experience.
              </Typography>
            </>
          )}
          <Button variant="contained" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  // Main view - show invite info and join button
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }}>
        {/* Organization info */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: stringToColor(inviteData.displayName),
              mx: 'auto',
              mb: 2,
            }}
          >
            {getInitials(inviteData.displayName)}
          </Avatar>
          <Typography variant="h5" gutterBottom>
            Join {inviteData.displayName}
          </Typography>
          {inviteData.admin.organizationName && inviteData.admin.facilityName && (
            <Typography variant="body2" color="text.secondary">
              {inviteData.admin.organizationName}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Info about what joining means */}
        <Alert severity="info" sx={{ mb: 3 }}>
          By joining this organization, you'll use their customized ChartMind prompts 
          when working at their facility.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Approval notice */}
        {inviteData.inviteLink.requiresApproval && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            This organization requires admin approval. Your request will be reviewed 
            before you can access their customized experience.
          </Alert>
        )}

        {/* Action buttons */}
        {!user ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please sign in to join this organization.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<LoginIcon />}
              onClick={handleLogin}
              fullWidth
            >
              Sign In to Continue
            </Button>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You're signed in as <strong>{user.email}</strong>
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleJoin}
              disabled={isJoining}
              startIcon={isJoining ? <CircularProgress size={20} /> : <BusinessIcon />}
              fullWidth
            >
              {isJoining ? 'Joining...' : 'Join Organization'}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default JoinOrganization;
